// Derivaciones puras sobre el timeline de una ejecución.
// Sin dependencias de React ni de servicios: lo que entra es la respuesta de
// `GET /executions/{id}/timeline` tal cual, y lo que sale es lo que la vista
// dibuja. Aislado para poder testear la agrupación por turnos contra corridas
// reales sin montar nada.

/**
 * Una entrada del timeline es una corrida de un paso o una espera.
 * Las esperas son las que parten una conversación en turnos: el flujo se queda
 * esperando un mensaje, llega, y lo que corre después es la respuesta a ese
 * mensaje.
 */
export const isWait = (entry) => entry?.kind === 'wait';
export const isStepRun = (entry) => entry?.kind === 'step';

/** Una espera que todavía no terminó: la ejecución está parada ahí ahora. */
export const isOpenWait = (entry) => isWait(entry) && !entry.completed_at;

const sumBy = (items, pick) => items.reduce((total, item) => total + (pick(item) || 0), 0);

const newTurn = (index, openingWait) => ({
  index,
  openingWait: openingWait || null,
  runs: [],
});

/**
 * Parte el timeline en turnos.
 *
 * Un turno es **una espera y el trabajo que corrió cuando esa espera se
 * destrabó**. La espera va al frente y no al final porque es lo que da inicio
 * al turno: llegó un mensaje, y estos pasos son la respuesta.
 *
 * El primer turno suele no tener espera de apertura — es el arranque de la
 * ejecución. Cuando sí la tiene es la ráfaga de mensajes, que junta lo que el
 * usuario escribió antes de que corriera el primer paso.
 *
 * El último turno puede no tener corridas: es la espera abierta, la ejecución
 * parada esperando ahora mismo.
 *
 * @param {Array} timeline entradas tal como vienen de la API, en orden de `seq`
 * @returns {Array<Object>} turnos con sus totales ya calculados
 */
export const groupIntoTurns = (timeline) => {
  const entries = Array.isArray(timeline) ? timeline : [];
  const turns = [];
  let current = newTurn(1, null);

  for (const entry of entries) {
    if (isWait(entry)) {
      // Un turno sin corridas ni espera es el arranque de una ejecución que
      // empezó esperando: no describe nada, así que no se emite.
      if (current.runs.length > 0 || current.openingWait) {
        turns.push(current);
      }
      current = newTurn(turns.length + 1, entry);
      continue;
    }
    if (isStepRun(entry)) {
      current.runs.push(entry);
    }
  }

  if (current.runs.length > 0 || current.openingWait) {
    turns.push(current);
  }

  return turns.map(summarizeTurn);
};

/**
 * Los totales de un turno.
 *
 * `endedAt` queda en null mientras el turno no haya cerrado su última corrida:
 * un turno en curso no tiene fin, y ponerle uno sería inventarlo.
 */
export const summarizeTurn = (turn) => {
  const { runs, openingWait } = turn;
  const first = runs[0];
  const last = runs[runs.length - 1];

  return {
    ...turn,
    startedAt: first?.started_at || openingWait?.started_at || null,
    endedAt: last?.completed_at || null,
    runningMs: sumBy(runs, (run) => run.duration_ms),
    waitingMs: openingWait?.duration_ms ?? null,
    coins: sumBy(runs, (run) => run.cost?.coins),
    failed: runs.filter((run) => run.status === 'failed').length,
    open: Boolean(openingWait && isOpenWait(openingWait) && runs.length === 0),
  };
};

/**
 * La espera en la que la ejecución está parada, si lo está.
 * Solo la última entrada puede serlo: si hubiera trabajo después, la espera
 * habría terminado.
 */
export const findOpenWait = (timeline) => {
  const entries = Array.isArray(timeline) ? timeline : [];
  const last = entries[entries.length - 1];
  return isOpenWait(last) ? last : null;
};

// Cuántas corridas seguidas sin nada que contar hacen falta para que valga la
// pena plegarlas. Con menos, la fila de resumen ocupa lo mismo que las filas
// que esconde.
const MIN_COLLAPSIBLE = 3;

// Una corrida que tardó más que esto se muestra siempre: es donde se va el
// tiempo de la ejecución.
const SLOW_RUN_MS = 1000;

/**
 * Si una entrada merece su propia fila en el timeline.
 *
 * Una ejecución conversacional corre noventa pasos de los cuales ochenta son
 * condiciones de milisegundos que salieron bien. Mostrarlos todos entierra los
 * cinco que importan; esconderlos sin decirlo miente. Se pliegan en una fila
 * que dice cuántos son y se puede abrir.
 */
export const isNotableEntry = (entry) => {
  if (isWait(entry)) return true;
  if (entry.status === 'failed' || entry.status === 'running') return true;
  if (entry.cost?.coins > 0) return true;
  return (entry.duration_ms || 0) >= SLOW_RUN_MS;
};

/**
 * Pliega las corridas seguidas que no tienen nada que contar.
 *
 * @returns {Array<Object>} filas: `{type: 'entry', entry}` o
 *   `{type: 'range', from, to, entries, durationMs}`
 */
export const collapseTimeline = (timeline) => {
  const entries = Array.isArray(timeline) ? timeline : [];
  const rows = [];
  let run = [];

  const flush = () => {
    if (run.length === 0) return;
    if (run.length < MIN_COLLAPSIBLE) {
      rows.push(...run.map((entry) => ({ type: 'entry', entry })));
    } else {
      rows.push({
        type: 'range',
        from: run[0].seq,
        to: run[run.length - 1].seq,
        entries: run,
        durationMs: run.reduce((total, entry) => total + (entry.duration_ms || 0), 0),
      });
    }
    run = [];
  };

  for (const entry of entries) {
    if (isNotableEntry(entry)) {
      flush();
      rows.push({ type: 'entry', entry });
    } else {
      run.push(entry);
    }
  }
  flush();

  return rows;
};

/** Busca por nombre o por tipo, que es lo que alguien recuerda de un paso. */
export const matchesQuery = (entry, query) => {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return true;
  return `${entry.name || ''} ${entry.type || ''}`.toLowerCase().includes(needle);
};

/**
 * Los pasos que no corrieron, filtrados y ordenados.
 *
 * Viven fuera del eje de tiempo a propósito: `decided_at` es el instante en
 * que se los descartó, no el fin de un intervalo. Un paso descartado no duró
 * nada, y darle ancho en una línea de tiempo es lo que producía los bloques
 * grises de la vista vieja.
 *
 * @param {Array} items `not_executed[]` tal como viene de la API
 * @param {Object} options `{query, reason, sort}` — `reason` en `'all' |
 *   'not_taken' | 'never_reached'`; `sort` en `'step_order' | 'decided_at' |
 *   'name'`
 */
export const prepareNotExecuted = (items, { query = '', reason = 'all', sort = 'step_order' } = {}) => {
  const list = Array.isArray(items) ? items : [];
  const needle = query.trim().toLowerCase();

  const filtered = list.filter((item) => {
    if (reason !== 'all' && item.reason !== reason) return false;
    if (!needle) return true;
    return `${item.name || ''} ${item.type || ''} ${item.decided_by || ''}`
      .toLowerCase()
      .includes(needle);
  });

  const sorted = [...filtered];
  if (sort === 'name') {
    sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } else if (sort === 'decided_at') {
    // Los que nunca se alcanzaron no tienen instante de decisión: van al
    // final, no al principio como los pondría un null.
    sorted.sort((a, b) => {
      if (!a.decided_at && !b.decided_at) return (a.step_order || 0) - (b.step_order || 0);
      if (!a.decided_at) return 1;
      if (!b.decided_at) return -1;
      return a.decided_at.localeCompare(b.decided_at);
    });
  } else {
    sorted.sort((a, b) => (a.step_order || 0) - (b.step_order || 0));
  }

  return sorted;
};

/** Cuántos hay de cada razón, para los filtros. */
export const countByReason = (items) => {
  const list = Array.isArray(items) ? items : [];
  return {
    all: list.length,
    not_taken: list.filter((item) => item.reason === 'not_taken').length,
    never_reached: list.filter((item) => item.reason === 'never_reached').length,
  };
};

/**
 * Duración legible. Por debajo del segundo se lee en milisegundos porque ahí
 * viven la mayoría de las corridas de paso; por encima del minuto, en minutos,
 * porque ahí viven las esperas.
 */
export const formatDuration = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 10000) return `${(ms / 1000).toFixed(2)} s`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms < 3600000) {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.round((ms % 60000) / 1000);
    return seconds === 60 ? `${minutes + 1}m 0s` : `${minutes}m ${seconds}s`;
  }
  // Una espera de dos horas leída en minutos obliga a dividir a ojo.
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.round((ms % 3600000) / 60000);
  return minutes === 60 ? `${hours + 1}h 0m` : `${hours}h ${minutes}m`;
};

/** Hora del día de un instante ISO, en la zona del que mira. */
export const formatTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
